import { Link, useLocation } from "wouter";
import { useAuth } from "../auth/AuthContext";
import { canSeeBilling, canSeeFleet } from "../auth/roleView";

const NAV_ITEMS = [
  { to: "/containers", label: "Containers" },
  { to: "/stacks", label: "Stacks" },
  { to: "/nodes", label: "Nodes" },
  { to: "/secrets", label: "Secrets" },
];

/** Register -> project -> service -> deploy, plus the Hetzner provisioning
 * half of that flow. Grouped apart from the fleet-management items above
 * since it's a distinct workflow (build/ship an app) rather than another
 * "things running in this fleet" list. */
const DEPLOY_NAV_ITEMS = [
  { to: "/projects", label: "Projects" },
  { to: "/instances", label: "Instances" },
  { to: "/provider-credentials", label: "Provider credentials" },
];

/** Users + audit-log administration — visible only to admins. This is UX
 * only (charter S-8): it saves a non-admin a dead-end click, it is never
 * the access control. The server enforces the real decision on every
 * request regardless of what this list says. */
const ADMIN_NAV_ITEMS = [
  { to: "/admin/users", label: "Users" },
  { to: "/admin/audit", label: "Audit log" },
];

function NavLink({ to, label, location }: { to: string; label: string; location: string }) {
  // wouter's Link only offers exact-match active styling out of the box;
  // match by prefix too so e.g. "Containers" stays highlighted while
  // looking at a single container's detail page.
  const isActive = location === to || location.startsWith(`${to}/`);
  return (
    <li>
      <Link href={to} className={`nav-sidebar__link${isActive ? " is-active" : ""}`} aria-current={isActive ? "page" : undefined}>
        {label}
      </Link>
    </li>
  );
}

export function NavSidebar() {
  const [location] = useLocation();
  const { session } = useAuth();
  const isAdmin = session?.role === "admin";
  // A billing_admin holds billing and nothing else, so the fleet sections are
  // hidden from it: every one of those pages could only ever answer 403. This
  // is coherence, not access control — the server is the control (S-8).
  const showFleet = canSeeFleet(session?.role);
  const showBilling = canSeeBilling(session?.role);

  return (
    <nav className="nav-sidebar" aria-label="Primary">
      {showFleet && (
        <ul>
          {NAV_ITEMS.map((item) => (
            <NavLink key={item.to} to={item.to} label={item.label} location={location} />
          ))}
        </ul>
      )}

      {showBilling && (
        <ul>
          <NavLink to="/billing" label="Billing" location={location} />
        </ul>
      )}

      {showFleet && (
        <div className="nav-sidebar__group">
          <p className="nav-sidebar__group-label" id="nav-sidebar-deploy-label">
            Deploy
          </p>
          <ul aria-labelledby="nav-sidebar-deploy-label">
            {DEPLOY_NAV_ITEMS.map((item) => (
              <NavLink key={item.to} to={item.to} label={item.label} location={location} />
            ))}
          </ul>
        </div>
      )}

      {isAdmin && (
        <div className="nav-sidebar__group">
          <p className="nav-sidebar__group-label" id="nav-sidebar-admin-label">
            Admin
          </p>
          <ul aria-labelledby="nav-sidebar-admin-label">
            {ADMIN_NAV_ITEMS.map((item) => (
              <NavLink key={item.to} to={item.to} label={item.label} location={location} />
            ))}
          </ul>
        </div>
      )}
    </nav>
  );
}
